import { AutocompleteChangeDetails, AutocompleteChangeReason, createFilterOptions } from "@mui/base/useAutocomplete/useAutocomplete";
import { Autocomplete } from "@trackman/web-shared-components";
import { SyntheticEvent, useCallback } from "react";

interface IAppAutoCompleteProps<T> {
    label: string;
    value: string;
    onValueChange: (newValue: string, newData?: T) => void;
    isLoading: boolean;
    allOptions: AutoCompleteOptionType<T>[];
    freeSolo?: boolean;
}

export type AutoCompleteOptionType<T> = string | { inputValue: string; title: string; data?: T };

export const AppAutoComplete = <T,>({ label, value, onValueChange, isLoading, allOptions, freeSolo = true }: IAppAutoCompleteProps<T>) => {
    const onValueChanged = useCallback((
        event: SyntheticEvent<Element, Event>,
        value: AutoCompleteOptionType<T> | string | AutoCompleteOptionType<T>[] | null,
        reason: AutocompleteChangeReason,
        details?: AutocompleteChangeDetails<AutoCompleteOptionType<T>> | undefined) => {
        const resolveValue = (val: AutoCompleteOptionType<T> | string | AutoCompleteOptionType<T>[] | null): { value: string; data?: T } => {
            if (!val) return { value: '', data: undefined };
            if (Array.isArray(val)) {
                const first = val[0];
                if (!first) return { value: '', data: undefined };
                if (typeof first === 'string') return { value: first, data: undefined };
                return { value: first.inputValue ?? first.title ?? '', data: first.data };
            }
            if (typeof val === 'string') return { value: val, data: undefined };
            return { value: val.inputValue ?? val.title ?? '', data: val.data };
        };

        const { value: resolvedValue, data: resolvedData } = resolveValue(value);

        switch (reason) {
            case 'selectOption':
            case 'createOption':
                onValueChange(resolvedValue, resolvedData);
                break;
            case 'removeOption':
            case 'clear':
                onValueChange('', undefined);
                break;
            default:
                onValueChange(resolvedValue, resolvedData);
                break;
        }
    }, [onValueChange]);

    const onBlurHandler = useCallback((event: SyntheticEvent<Element, Event>) => {
        const target = event.target as HTMLInputElement;
        const inputValue = target?.value || '';
        
        if (inputValue && inputValue !== value) {
            onValueChange(inputValue, undefined);
        }
    }, [value, onValueChange]);

    const filter = createFilterOptions<AutoCompleteOptionType<T>>();

    const selectedValue: AutoCompleteOptionType<T> | string = allOptions.find(tag => {
        if (typeof tag === 'string') return tag === value;
        return (tag.inputValue ?? tag.title) === value;
    }) ?? (value || '');

    return (
        <Autocomplete
            label={label}
            freeSolo={freeSolo}
            value={selectedValue}
            disabled={isLoading}
            selectOnFocus
            handleHomeEndKeys
            onBlur={onBlurHandler}
            options={allOptions || [] as AutoCompleteOptionType<T>[]}
            filterOptions={(options, params) => {
                const filtered = filter(options, params);
                const { inputValue } = params;
                const isExisting = options.some(option => {
                    if (typeof option === 'string') return option === inputValue;
                    return (option.inputValue ?? option.title) === inputValue;
                });
                if (inputValue !== '' && !isExisting && freeSolo) {
                    filtered.push({ inputValue, title: `Add "${inputValue}"` });
                }
                return filtered;
            }}
            getOptionLabel={(option) => typeof option === 'string' ? option : option.inputValue ?? option.title}
            onChange={onValueChanged}
            renderOption={(props, option) => (
                <li {...props}>{typeof option === 'string' ? option : option.title}</li>
            )}
        />
    );
};